import { useToastContext } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  OperatorByIdDocument,
  OperatorByIdQuery,
  OperatorManyDocument,
  useOperatorUpdateMutation,
  useTagGroupOneQuery,
  useTagManyQuery,
} from "gql/generated";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMultilingualString } from "shared/utils";

export const useBackOfficeTags = (
  operator: OperatorByIdQuery["operatorById"],
  operatorEntityId: string | undefined,
  organizationId: string | undefined,
) => {
  const { toastFactory } = useToastContext();
  const { data: tagGroupData } = useTagGroupOneQuery({
    variables: {
      filter: {
        key: EnumTagGroupKey.BackOffice,
        type: EnumTagGroupType.OrganizationSpecific,
      },
    },
  });
  const {
    data: tagData,
    loading: loadingTags,
    error: errorTags,
  } = useTagManyQuery({
    variables: {
      filter: {
        tagGroup: tagGroupData?.tagGroupOne?._id
          ? [tagGroupData.tagGroupOne._id]
          : null,
        owner: { entityId: organizationId, type: EnumOwnerType.Organization },
        entityTypes: [EnumTagEntityTypes.Operator],
      },
      limit: 10000,
    },
  });

  const backOfficeTagOptions = useMemo(
    () =>
      tagData?.tagMany
        ? _.sortBy(
            tagData.tagMany.map((tag) => ({
              value: tag._id,
              label: getMultilingualString(tag.name),
            })),
            "label",
          )
        : [],
    [tagData?.tagMany],
  );
  const defaultBackOfficeTagIds = useMemo(
    () =>
      operator.tags ? operator.tags.organizationSpecific.map((t) => t._id) : [],
    [operator],
  );

  // The following useStates are so the user doesn't have to wait until the
  // API call succeeds to see the change in the UI
  const [filterableBackOfficeTags, setFilterableBackOfficeTags] =
    useState(backOfficeTagOptions);
  const [selectedBackOfficeTagIds, setSelectedBackOfficeTagIds] = useState<
    string[]
  >(defaultBackOfficeTagIds);

  const { t } = useTranslation();
  const [updateOperator] = useOperatorUpdateMutation({
    refetchQueries: [OperatorByIdDocument, OperatorManyDocument],
  });

  const handleUpdateSelectedTagIds = async (newTagIds: string[]) => {
    try {
      setSelectedBackOfficeTagIds(newTagIds);
      await updateOperator({
        variables: {
          entityId: operatorEntityId ?? "",
          record: {
            tags: {
              businessType: operator.tags?.businessType.map((t) => t._id),
              global: operator.tags?.global.map((t) => t._id),
              organizationSpecific: newTagIds,
            },
          },
        },
      });
    } catch {
      setSelectedBackOfficeTagIds(defaultBackOfficeTagIds);
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    }
  };

  const handleTagInputValueSearch = (search: string) => {
    const inputValue = (search.toLowerCase().split(",").pop() ?? "").trim();
    const filtered = backOfficeTagOptions.filter((item) =>
      item.label.toLowerCase().includes(inputValue),
    );
    setFilterableBackOfficeTags(
      filtered.length > 0 ? filtered : backOfficeTagOptions,
    );
  };

  const handleComboboxOpenChange = () => {
    setFilterableBackOfficeTags(backOfficeTagOptions);
  };

  useEffect(() => {
    setSelectedBackOfficeTagIds(defaultBackOfficeTagIds);
  }, [defaultBackOfficeTagIds]);

  useEffect(() => {
    setFilterableBackOfficeTags(backOfficeTagOptions);
  }, [backOfficeTagOptions]);

  return {
    onUpdateSelectedTagIds: handleUpdateSelectedTagIds,
    onTagInputValueSearch: handleTagInputValueSearch,
    onComboboxOpenChange: handleComboboxOpenChange,
    selectedBackOfficeTagIds,
    filterableBackOfficeTags,
    loadingTags,
    errorTags,
  };
};
